import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { FLAG_DEFAULTS, type FeatureFlags } from '@cca/corpus-shared';
import { api, type InstanceInfo } from '../api/client';

/**
 * Loads the instance identity (GET /api/instance) and shares it. An instance is
 * configuration, not code (ADR-006): the worker decides the name, genre, theme and
 * feature flags, the SPA just renders them. The theme is re-applied to
 * <html data-theme> at runtime so a re-theme needs no rebuild.
 *
 * `useApplyFeatures()` patches the live feature flags in place — used after a
 * registrar flips one, so the change takes effect across the app immediately (the
 * gated entry points read `features` from here), with no page reload. It applies
 * the toggle's authoritative response directly rather than re-fetching, avoiding
 * any KV read-after-write lag.
 *
 * Falls back to the build-time default (Liturgical Commission, all features on) if
 * the call fails — the login screen must still render so a user can sign in.
 */
const FALLBACK: InstanceInfo = {
  instanceName: 'Liturgical Commission',
  genre: 'liturgy',
  theme: 'liturgical',
  // Features default ON if /api/instance can't be reached — never dark-out a
  // shipped feature on a fetch blip (matches the worker's fail-safe).
  features: FLAG_DEFAULTS,
};

const InstanceCtx = createContext<InstanceInfo>(FALLBACK);
const ApplyFeaturesCtx = createContext<(features: FeatureFlags) => void>(() => {});

export function useInstance(): InstanceInfo {
  return useContext(InstanceCtx);
}

/** Apply new feature-flag values immediately (e.g. the response of a toggle). */
export function useApplyFeatures(): (features: FeatureFlags) => void {
  return useContext(ApplyFeaturesCtx);
}

export function InstanceProvider({ children }: { children: ReactNode }) {
  const [instance, setInstance] = useState<InstanceInfo>(FALLBACK);

  const applyFeatures = useCallback((features: FeatureFlags) => {
    setInstance((prev) => ({ ...prev, features }));
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .instance()
      .then((info) => {
        if (!alive) return;
        setInstance(info);
        if (info.theme) document.documentElement.dataset.theme = info.theme;
      })
      .catch(() => {
        // Keep the build-time default; login stays usable.
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <InstanceCtx.Provider value={instance}>
      <ApplyFeaturesCtx.Provider value={applyFeatures}>{children}</ApplyFeaturesCtx.Provider>
    </InstanceCtx.Provider>
  );
}
